/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, September 26th 2022, 5:37:55 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { getDefaultFieldValidation } from "lib/factories";
import { FieldValidator, FormField, DatasetReference } from "lib/schemas";
import { DatasetReferenceSelectInput } from "datasets/components/DatasetReferenceSelectInput";
import { useDatasetSchemaAPI } from "datasets/services/DatasetSchemaAPI";
import { useFieldValidatorsAPI } from "form-builder/services";
import { useFormBuilderStore, currentFormSelector } from "form-builder/stores";
import { ValidationIcon } from "theme/icons";
import { COLORSCHEME } from "theme/styles";
import { OSAPIForm, TextFormInput, TextAreaFormInput, DropDownFormInput } from "ui-widgets/forms";
import { useOSAPIFormContext } from "ui-widgets/forms/providers";
import { StyledOSDialog, StyledOSDialogProps, InputHelpDialogWrapper, EmphasisText } from "ui-widgets";

interface FieldValidatorFormProps {
  fieldValidator: FieldValidator;
}
const FieldValidatorForm = ({ fieldValidator }: FieldValidatorFormProps) => {
  const {
    useFormReturn: { watch: watchFieldForm, setValue: setFieldFormValue },
  } = useOSAPIFormContext<FormField>();
  const fieldLabel = watchFieldForm("label");
  const validatorIds = watchFieldForm("validators");

  const addFieldValidator = useFormBuilderStore((state) => state.addFieldValidator);
  const currentFormConfiguration = useFormBuilderStore(currentFormSelector);

  const fieldValidatorData =
    fieldValidator ?? getDefaultFieldValidation({ organizationId: currentFormConfiguration.artifact_org });

  const onSaveFieldValidator = (fieldValidator: FieldValidator) => {
    addFieldValidator(fieldValidator);
    setFieldFormValue("validators", [...validatorIds, fieldValidator.id], { shouldDirty: true });
  };

  const { useFieldValidatorAPIForm } = useFieldValidatorsAPI();
  const useAPIFormReturn = useFieldValidatorAPIForm({
    fieldValidatorId: null,
    fieldValidatorDefault: fieldValidatorData,
    updateFieldValidatorFromAPICallback: onSaveFieldValidator,
  });

  const {
    useFormReturn: { setValue: setFieldValidatorValue },
  } = useAPIFormReturn;

  const onChangeLookupColumn = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValidatorValue("dataset_lookup_settings.lookup_column", event.target.value);
  };

  const { useAPIFetchDatasetSchema } = useDatasetSchemaAPI();

  const { fetchItem: fetchDatasetSchema, apiItem: referencedDatasetSchema } = useAPIFetchDatasetSchema();

  const [isDatabaseTableReference, setIsDatabaseTableReference] = React.useState(false);

  const onChangeDatasetReference = (datasetReference: DatasetReference) => {
    if (!datasetReference?.ref_metadata?.json_dataset_schema) {
      setIsDatabaseTableReference(datasetReference?.ref_metadata?.table_name);
      return;
    }

    fetchDatasetSchema(null, {
      apiQueryParams: { artifact_guid: datasetReference?.ref_metadata?.json_dataset_schema },
    });
    setIsDatabaseTableReference(false);
  };

  const referencedDatasetFieldSchemas = referencedDatasetSchema?.[0]?.json_schema?.properties;

  return (
    <div className="os-field-validator-form-container">
      <OSAPIForm useAPIFormReturn={useAPIFormReturn}>
        <InputHelpDialogWrapper
          helpTitle="Validator Name"
          helpContent="Give this custom validator a name you can easily recognise and refer to."
        >
          <TextFormInput<FieldValidator> schema_field_key="name" label="Validator Name" />
        </InputHelpDialogWrapper>
        <InputHelpDialogWrapper
          helpTitle="Dataset"
          helpContent={
            <>
              Choose or Create a dataset to compare against when validating data for the{" "}
              <EmphasisText>{fieldLabel}</EmphasisText> field.
            </>
          }
        >
          <DatasetReferenceSelectInput<FieldValidator>
            schema_field_key="validation_dataset_reference"
            label="Dataset Connection"
            settingsType="data_retrieval"
            onChangeCallBack={onChangeDatasetReference}
            onLoadInitial={onChangeDatasetReference}
          />
        </InputHelpDialogWrapper>
        <InputHelpDialogWrapper
          helpTitle="Lookup Column"
          helpContent="Choose the column in the configured dataset that should be used to compare submitted field values against."
        >
          {isDatabaseTableReference ? (
            <TextFormInput schema_field_key="dataset_lookup_settings.lookup_column" label="Lookup Column" />
          ) : Boolean(referencedDatasetFieldSchemas) ? (
            <DropDownFormInput
              schema_field_key="dataset_lookup_settings.lookup_column"
              options={referencedDatasetFieldSchemas}
              onChange={onChangeLookupColumn}
              getOptionLabel={(fieldSchemaKey) =>
                referencedDatasetFieldSchemas[fieldSchemaKey]?.title ?? fieldSchemaKey
              }
              label="Lookup Column"
            />
          ) : null}
        </InputHelpDialogWrapper>
        <InputHelpDialogWrapper
          helpTitle="Error Message"
          helpContent="Give a custom error message to show the user when validation against the configured dataset fails."
        >
          <TextAreaFormInput<FieldValidator> schema_field_key="error_message" label="Error Message" />
        </InputHelpDialogWrapper>
      </OSAPIForm>
    </div>
  );
};

type FieldValidatorFormModalProps = Pick<StyledOSDialogProps, "renderOpenButton"> &
  Pick<FieldValidatorFormProps, "fieldValidator">;

export const FieldValidatorFormModal = ({ renderOpenButton, fieldValidator }: FieldValidatorFormModalProps) => (
  <StyledOSDialog
    open={null}
    renderOpenButton={renderOpenButton}
    popUpTitle={<>Custom Field Validator</>}
    popUpIcon={<ValidationIcon fill={COLORSCHEME.primary} />}
  >
    <FieldValidatorForm fieldValidator={fieldValidator} />
  </StyledOSDialog>
);
